import { z } from 'zod';
import {
  DeviceSyncField,
  BusinessAddressSchema,
  type BusinessAddressType,
} from '@am3nd-org/pointcraft-types';

import { ApiMethods, apiSenderV2, apiEndpoints } from 'src/utils/axios';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { getSessionGeneration, isSessionGenerationCurrent } from 'src/auth/session-lifecycle';

import { syncDeviceSyncV4Fields } from './device-sync-v4.service';
import {
  BusinessMutationApiError,
  normalizeBusinessMutationError,
} from './business-mutation-errors';

export type BusinessProfilePatchRequest = {
  email?: string;
  phone?: string;
  address?: BusinessAddressType;
};

export type BusinessProfilePatchResponse = {
  email: string;
  phone: string;
  address: BusinessAddressType;
};

const BusinessProfilePatchResponseSchema = z.object({
  email: z.string().email(),
  phone: z.string().min(1),
  address: BusinessAddressSchema,
});

function applyProfile(profile: BusinessProfilePatchResponse): void {
  useDeviceSyncStore.getState().updateBusinessData((business) => ({
    ...business,
    businessInfo: {
      ...business.businessInfo,
      email: profile.email,
      phone: profile.phone,
      address: profile.address,
    },
  }));
}

function refreshBusinessDataInBackground(): void {
  syncDeviceSyncV4Fields([DeviceSyncField.BUSINESS_DATA]).catch((error) => {
    console.warn('[BusinessProfile] Profile updated but background refresh failed', error);
  });
}

export async function updateBusinessProfile(
  patch: BusinessProfilePatchRequest
): Promise<BusinessProfilePatchResponse> {
  const generation = getSessionGeneration();
  try {
    const response = await apiSenderV2(
      apiEndpoints.business.profile,
      ApiMethods.PATCH,
      patch,
      true,
      null,
      { throwOnError: true }
    );
    const parsed = BusinessProfilePatchResponseSchema.safeParse(response);
    if (!parsed.success) {
      throw new BusinessMutationApiError(
        'UNKNOWN_ERROR',
        `The business profile response was invalid: ${parsed.error.message}`
      );
    }

    if (!isSessionGenerationCurrent(generation)) return parsed.data;
    applyProfile(parsed.data);
    refreshBusinessDataInBackground();
    return parsed.data;
  } catch (error) {
    throw normalizeBusinessMutationError(error, {
      invalidRequestMessage: 'Check the changed profile details and try again.',
      fallbackMessage: 'Business profile could not be updated. Please try again.',
    });
  }
}
